import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { purchaseApi } from "./api";
import { dateInput, dateText, errorMessage, money } from "./utils";

import "./InvoicePage.css";

const getEmptyForm = () => ({
  vendor: "",
  billNo: "",
  invoiceDate: dateInput(),
  projectId: "",
  projectName: "",
  company: "SDC",
  workStatus: "Not Done",
  workDescription: "",
  accountHead: "",
  taxableAmount: 0,
  gstPercent: 18,
  gstType: "CGST_SGST",
  quotation: "",
  proformaInvoice: "",
  status: "Draft",
  remark: "",
});

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export default function InvoicePage() {
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [financeMasters, setFinanceMasters] = useState({});
  const [form, setForm] = useState(getEmptyForm());
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pay, setPay] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("");
  const [quarter, setQuarter] = useState("");
  const [month, setMonth] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");

      // Invoice, Vendor and Project
      const [invoiceResponse, vendorResponse, projectResponse] =
        await Promise.all([
          purchaseApi.invoices.list({
            search,
          }),
          purchaseApi.vendors.list(),
          purchaseApi.projects.list(),
        ]);

      const invoiceData = invoiceResponse?.data || {};
      const vendorData = vendorResponse?.data || {};
      const projectData = projectResponse?.data;

      setRows(
        Array.isArray(invoiceData) ? invoiceData : invoiceData.invoices || [],
      );

      setVendors(
        Array.isArray(vendorData) ? vendorData : vendorData.vendors || [],
      );

      const projectRows = Array.isArray(projectData)
        ? projectData
        : projectData?.projects || projectData?.data || [];

      setProjects(projectRows);

      // ============================
      // FINANCE MASTER
      // ============================

      try {
        const response = await purchaseApi.financeMasters.list();

        console.log("FINANCE MASTER RAW:", response);

        console.log("FINANCE MASTER RESPONSE DATA:", response?.data);

        const masterData = response?.data?.data || response?.data || {};

        const expenseHeads = Array.isArray(masterData?.expenseHeads)
          ? masterData.expenseHeads
          : [];

        console.log("EXPENSE HEADS FOUND:", expenseHeads);

        setFinanceMasters({
          ...masterData,
          expenseHeads,
        });
      } catch (financeError) {
        console.error("FINANCE MASTER ERROR:", financeError);

        setFinanceMasters({
          expenseHeads: [],
        });
      }
    } catch (e) {
      console.error("Purchase invoice load error:", e);

      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filterMeta = useMemo(() => {
    const map = {};

    rows.forEach((row) => {
      if (!row.invoiceDate) return;

      const d = new Date(row.invoiceDate);

      if (Number.isNaN(d.getTime())) {
        return;
      }

      const y = d.getFullYear();

      const m = d.getMonth() + 1;

      if (!map[y]) {
        map[y] = {
          months: new Set(),

          quarters: new Set(),
        };
      }

      map[y].months.add(m);

      map[y].quarters.add(Math.ceil(m / 3));
    });

    return map;
  }, [rows]);

  const years = useMemo(
    () =>
      Object.keys(filterMeta)

        .map(Number)

        .sort((a, b) => b - a),

    [filterMeta],
  );

  const availableMonths =
    year && filterMeta[year]
      ? Array.from(filterMeta[year].months).sort((a, b) => a - b)
      : [];

  const availableQuarters =
    year && filterMeta[year]
      ? Array.from(filterMeta[year].quarters).sort((a, b) => a - b)
      : [];

  /* =======================================================







     FILTERED ROWS







  ======================================================= */

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!year && !quarter && !month) {
        return true;
      }

      if (!row.invoiceDate) {
        return false;
      }

      const d = new Date(row.invoiceDate);

      if (Number.isNaN(d.getTime())) {
        return false;
      }

      const rowYear = d.getFullYear();

      const rowMonth = d.getMonth() + 1;

      const rowQuarter = Math.ceil(rowMonth / 3);

      if (year && rowYear !== Number(year)) {
        return false;
      }

      if (quarter && rowQuarter !== Number(quarter)) {
        return false;
      }

      if (month && rowMonth !== Number(month)) {
        return false;
      }

      return true;
    });
  }, [rows, year, quarter, month]);

  /* =======================================================







     SUMMARY







  ======================================================= */

  const summary = useMemo(() => {
    return filteredRows.reduce(
      (result, row) => {
        result.totalPurchase += Number(row.invoiceAmount || 0);

        result.totalPaid += Number(row.paidAmount || 0);

        result.totalOutstanding += Number(row.balanceAmount || 0);

        return result;
      },

      {
        totalPurchase: 0,

        totalPaid: 0,

        totalOutstanding: 0,
      },
    );
  }, [filteredRows]);

  /* =======================================================







     CALCULATED AMOUNTS







  ======================================================= */

  const calculated = useMemo(() => {
    const taxable = Number(form.taxableAmount || 0);

    const gstPercent = Number(form.gstPercent || 0);

    const gstAmount = taxable * (gstPercent / 100);

    const gstType = form.gstType === "IGST" ? "IGST" : "CGST_SGST";

    const cgst = gstType === "CGST_SGST" ? gstAmount / 2 : 0;

    const sgst = gstType === "CGST_SGST" ? gstAmount / 2 : 0;

    const igst = gstType === "IGST" ? gstAmount : 0;

    return {
      taxable,

      gstAmount,

      cgst,

      sgst,

      igst,

      invoiceAmount: taxable + gstAmount,
    };
  }, [form.taxableAmount, form.gstPercent, form.gstType]);

  /* =======================================================







     SELECT OPTIONS







  ======================================================= */

  const vendorOptions = useMemo(
    () =>
      vendors

        .filter((vendor) => vendor.status !== "Inactive")

        .map((vendor) => ({
          value: vendor._id,

          label: vendor.vendorName,
        }))

        .sort((a, b) => a.label.localeCompare(b.label)),

    [vendors],
  );

  const projectOptions = useMemo(
    () =>
      projects

        .map((project) => ({
          value: project._id,

          label: project.name || project.projectName || "",
        }))

        .filter((option) => option.label)

        .sort((a, b) => a.label.localeCompare(b.label)),

    [projects],
  );

  const accountHeadOptions = useMemo(() => {
    const heads = Array.isArray(financeMasters?.expenseHeads)
      ? financeMasters.expenseHeads
      : [];

    return heads

      .map((head) => String(head || "").trim())

      .filter(Boolean)

      .sort((a, b) => a.localeCompare(b))

      .map((head) => ({
        value: head,

        label: head,
      }));
  }, [financeMasters]);

  /* =======================================================







     NEW INVOICE







  ======================================================= */

  const openCreate = () => {
    setEditing(null);

    setForm(getEmptyForm());

    setAttachments([]);

    setExistingAttachments([]);

    setError("");

    setOpen(true);
  };

  /* =======================================================







     EDIT







  ======================================================= */

  const edit = async (id) => {
    try {
      setError("");

      const { data } = await purchaseApi.invoices.get(id);

      const invoice = data?.invoice;

      if (!invoice) {
        setError("Invoice not found.");

        return;
      }

      setForm({
        ...getEmptyForm(),

        ...invoice,

        invoiceDate: dateInput(invoice.invoiceDate),

        vendor: invoice.vendor?._id || invoice.vendor || "",

        projectId: invoice.projectId?._id || invoice.projectId || "",

        projectName: invoice.projectName || "",

        quotation: invoice.quotation?._id || invoice.quotation || "",

        proformaInvoice:
          invoice.proformaInvoice?._id || invoice.proformaInvoice || "",

        company: "SDC",
      });

      setExistingAttachments(
        Array.isArray(invoice.attachments) ? invoice.attachments : [],
      );

      setAttachments([]);

      setEditing(id);

      setOpen(true);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =======================================================







     FILE CHANGE







  ======================================================= */

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    setAttachments((current) => [...current, ...files]);

    event.target.value = "";
  };

  const removeNewAttachment = (index) => {
    setAttachments((current) => current.filter((_, i) => i !== index));
  };

  /* =======================================================







     OPEN ATTACHMENT







  ======================================================= */

  const openAttachment = async (file) => {
    try {
      setError("");

      if (!file?.fileName) {
        setError("Document file name not found.");

        return;
      }

      const response = await purchaseApi.invoices.openFile(file.fileName);

      const contentType =
        response.headers?.["content-type"] ||
        file.mimeType ||
        "application/octet-stream";

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const blobUrl = window.URL.createObjectURL(blob);

      const newTab = window.open(blobUrl, "_blank");

      if (!newTab) {
        window.URL.revokeObjectURL(blobUrl);

        setError("Popup blocked. Please allow popups to open the document.");

        return;
      }

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      console.error("Open invoice attachment:", e);

      setError(errorMessage(e));
    }
  };

  /* =======================================================







     SAVE







  ======================================================= */

  const save = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      setError("");

      if (!form.vendor) {
        setError("Please select vendor.");

        return;
      }

      if (!String(form.billNo || "").trim()) {
        setError("Vendor bill number is required.");

        return;
      }

      if (!String(form.accountHead || "").trim()) {
        setError("Please select Account Head.");

        return;
      }

      if (Number(form.taxableAmount || 0) <= 0) {
        setError("Taxable amount must be greater than zero.");

        return;
      }

      const payload = new FormData();

      payload.append("vendor", form.vendor);

      payload.append("billNo", String(form.billNo || "").trim());

      payload.append("invoiceDate", form.invoiceDate || dateInput());

      payload.append("projectId", form.projectId || "");

      /*







       * Company frontend aur







       * backend dono side fixed.







       */

      payload.append("company", "SDC");

      payload.append("workStatus", form.workStatus || "Not Done");

      payload.append("workDescription", form.workDescription || "");

      payload.append("accountHead", String(form.accountHead || "").trim());

      payload.append("taxableAmount", String(form.taxableAmount || 0));

      payload.append("gstPercent", String(form.gstPercent ?? 18));

      payload.append("gstType", form.gstType || "CGST_SGST");

      payload.append("quotation", form.quotation || "");

      payload.append("proformaInvoice", form.proformaInvoice || "");

      payload.append("status", form.status || "Draft");

      payload.append("remark", form.remark || "");

      attachments.forEach((file) => {
        payload.append("attachments", file);
      });

      if (editing) {
        await purchaseApi.invoices.update(editing, payload);
      } else {
        await purchaseApi.invoices.create(payload);
      }

      setOpen(false);

      setEditing(null);

      setForm(getEmptyForm());

      setAttachments([]);

      setExistingAttachments([]);

      await load();
    } catch (e) {
      console.error("Save purchase invoice:", e);

      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================







     DELETE







  ======================================================= */

  const remove = async (id) => {
    if (!window.confirm("Delete this purchase invoice?")) {
      return;
    }

    try {
      setError("");

      await purchaseApi.invoices.remove(id);

      await load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  /* =======================================================







     ADD PAYMENT







  ======================================================= */

  const savePayment = async (event) => {
    event.preventDefault();

    if (!pay) return;

    try {
      setPaymentSaving(true);

      setError("");

      await purchaseApi.invoices.addPayment(pay.id, {
        amount: pay.amount,

        date: pay.date,

        paymentMode: pay.paymentMode,

        referenceNo: pay.referenceNo,

        remark: pay.remark,
      });

      setPay(null);

      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPaymentSaving(false);
    }
  };

  /* =======================================================







     FILE NAME







  ======================================================= */

  const getAttachmentName = (file, index) =>
    file?.originalName || file?.fileName || `Document ${index + 1}`;

  /* =======================================================







     RENDER







  ======================================================= */

  return (
    <div className="invoice-page">
      {/* ===================================================







          HEADER







      =================================================== */}

      <div className="invoice-head">
        <div>
          <h1>Purchase Invoice</h1>

          <p>Vendor bills, GST, project work and payment tracking.</p>
        </div>

        <button
          type="button"
          className="invoice-btn primary"
          onClick={openCreate}
        >
          + Create Invoice
        </button>
      </div>

      {error && <div className="invoice-error">{error}</div>}

      {/* ===================================================







          FILTER







      =================================================== */}

      <div className="invoice-card invoice-filter-card">
        <div className="invoice-filter-row">
          <div className="invoice-field invoice-search">
            <label>Search</label>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  load();
                }
              }}
              placeholder="Invoice / Bill / Vendor / Project"
            />
          </div>

          <div className="invoice-field">
            <label>Year</label>

            <select
              value={year}
              onChange={(e) => {
                setYear(e.target.value);

                setQuarter("");

                setMonth("");
              }}
            >
              <option value="">All Years</option>

              {years.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="invoice-field">
            <label>Quarter</label>

            <select
              value={quarter}
              disabled={!year}
              onChange={(e) => setQuarter(e.target.value)}
            >
              <option value="">All Quarters</option>

              {availableQuarters.map((item) => (
                <option key={item} value={item}>
                  Q{item}
                </option>
              ))}
            </select>
          </div>

          <div className="invoice-field">
            <label>Month</label>

            <select
              value={month}
              disabled={!year}
              onChange={(e) => setMonth(e.target.value)}
            >
              <option value="">All Months</option>

              {availableMonths.map((item) => (
                <option key={item} value={item}>
                  {MONTHS[item - 1]}
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="invoice-btn" onClick={load}>
            Search
          </button>

          <button
            type="button"
            className="invoice-btn"
            onClick={() => {
              setSearch("");

              setYear("");

              setQuarter("");

              setMonth("");
            }}
          >
            Clear
          </button>
        </div>

        {/* ===============================================







            SUMMARY







        =============================================== */}

        <div className="invoice-summary">
          <div className="invoice-stat">
            <small>Invoices</small>

            <strong>{filteredRows.length}</strong>
          </div>

          <div className="invoice-stat">
            <small>Total Purchase</small>

            <strong>{money(summary.totalPurchase)}</strong>
          </div>

          <div className="invoice-stat">
            <small>Paid</small>

            <strong>{money(summary.totalPaid)}</strong>
          </div>

          <div className="invoice-stat">
            <small>Outstanding</small>

            <strong>{money(summary.totalOutstanding)}</strong>
          </div>
        </div>
      </div>

      {/* ===================================================







          TABLE







      =================================================== */}

      <div className="invoice-card invoice-table-card">
        <div className="invoice-table-wrap">
          <table className="invoice-table">
            <thead>
              <tr>
                <th>INVOICE</th>

                <th>BILL NO.</th>

                <th>VENDOR</th>

                <th>DATE</th>

                <th>PROJECT</th>

                <th>TOTAL</th>

                <th>PAID</th>

                <th>BALANCE</th>

                <th>PAYMENT</th>

                <th>DOCUMENT</th>

                <th>ACTION</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="invoice-empty">
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="invoice-empty">
                    No purchase invoices found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row._id}>
                    <td>
                      <b>{row.invoiceNo}</b>
                    </td>

                    <td>{row.billNo || "-"}</td>

                    <td>{row.vendorName || "-"}</td>

                    <td>{dateText(row.invoiceDate)}</td>

                    <td>{row.projectName || "-"}</td>

                    <td className="invoice-amount">
                      {money(row.invoiceAmount)}
                    </td>

                    <td className="invoice-amount paid">
                      {money(row.paidAmount)}
                    </td>

                    <td className="invoice-amount balance">
                      {money(row.balanceAmount)}
                    </td>

                    <td>
                      <span
                        className={`invoice-payment-status ${String(
                          row.paymentStatus || "",
                        )
                          .toLowerCase()

                          .replace(/\s+/g, "-")}`}
                      >
                        {row.paymentStatus || "Unpaid"}
                      </span>
                    </td>

                    <td>
                      {Array.isArray(row.attachments) &&
                      row.attachments.length > 0 ? (
                        <button
                          type="button"
                          className="invoice-document-link"
                          onClick={() => openAttachment(row.attachments[0])}
                        >
                          📎 {row.attachments.length} Document
                          {row.attachments.length > 1 ? "s" : ""}
                        </button>
                      ) : (
                        <span className="invoice-no-document">-</span>
                      )}
                    </td>

                    <td>
                      <div className="invoice-actions">
                        <button type="button" onClick={() => edit(row._id)}>
                          Edit
                        </button>

                        <button type="button" onClick={() => setPreview(row)}>
                          Preview
                        </button>

                        {Number(row.balanceAmount || 0) > 0 && (
                          <button
                            type="button"
                            className="payment"
                            onClick={() =>
                              setPay({
                                id: row._id,

                                invoiceNo: row.invoiceNo,

                                balanceAmount: row.balanceAmount,

                                amount: "",

                                date: dateInput(),

                                paymentMode: "NEFT",

                                referenceNo: "",

                                remark: "",
                              })
                            }
                          >
                            Payment
                          </button>
                        )}

                        <button
                          type="button"
                          className="danger"
                          onClick={() => remove(row._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===================================================







          CREATE / EDIT MODAL







      =================================================== */}

      {open && (
        <div className="invoice-modal-backdrop">
          <form className="invoice-modal" onSubmit={save}>
            <div className="invoice-modal-head">
              <div>
                <h2>
                  {editing
                    ? "Edit Purchase Invoice"
                    : "Create Purchase Invoice"}
                </h2>

                <p>
                  Enter vendor bill, GST, project and supporting document
                  details.
                </p>
              </div>

              <button
                type="button"
                className="invoice-modal-close"
                onClick={() => {
                  setOpen(false);

                  setEditing(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="invoice-modal-body">
              {/* ===========================================







                  BASIC DETAILS







              =========================================== */}

              <div className="invoice-section">
                <div className="invoice-section-title">Invoice Details</div>

                <div className="invoice-form-grid invoice-details-one-row">
                  <div className="invoice-form-field">
                    <label>Vendor *</label>

                    <Select
                      classNamePrefix="invoice-select"
                      placeholder="Search vendor..."
                      isSearchable
                      options={vendorOptions}
                      value={
                        vendorOptions.find(
                          (option) => option.value === form.vendor,
                        ) || null
                      }
                      onChange={(option) =>
                        setForm((current) => ({
                          ...current,

                          vendor: option?.value || "",
                        }))
                      }
                    />
                  </div>

                  <div className="invoice-form-field">
                    <label>Vendor Bill No. *</label>

                    <input
                      required
                      value={form.billNo || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,

                          billNo: e.target.value,
                        })
                      }
                      placeholder="Enter bill no."
                    />
                  </div>

                  <div className="invoice-form-field">
                    <label>Invoice Date</label>

                    <input
                      type="date"
                      value={form.invoiceDate || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,

                          invoiceDate: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* PROJECT OPTIONAL */}

                  <div className="invoice-form-field">
                    <label>
                      Project
                      <small> (Optional)</small>
                    </label>

                    <Select
                      classNamePrefix="invoice-select"
                      placeholder="Search project..."
                      isSearchable
                      isClearable
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (option) => option.value === form.projectId,
                        ) || null
                      }
                      onChange={(option) =>
                        setForm((current) => ({
                          ...current,

                          projectId: option?.value || "",

                          projectName: option?.label || "",
                        }))
                      }
                    />
                  </div>

                  {/* COMPANY FIXED */}

                  <div className="invoice-form-field">
                    <label>Company</label>

                    <input value="SDC" readOnly className="invoice-readonly" />
                  </div>
                </div>
              </div>

              <div className="invoice-section">
                <div className="invoice-section-title">Work Details</div>

                <div className="invoice-form-grid">
                  <div className="invoice-form-field">
                    <label>Work Status</label>

                    <select
                      value={form.workStatus}
                      onChange={(e) =>
                        setForm({
                          ...form,

                          workStatus: e.target.value,
                        })
                      }
                    >
                      {[
                        "Not Done",
                        "In Progress",
                        "Partially Done",
                        "Completed",
                        "NA",
                      ].map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="invoice-form-field">
                    <label>Account Head *</label>

                    <Select
                      classNamePrefix="invoice-select"
                      placeholder="Search account head..."
                      isSearchable
                      isClearable
                      options={accountHeadOptions}
                      value={
                        accountHeadOptions.find(
                          (option) => option.value === form.accountHead,
                        ) || null
                      }
                      onChange={(option) =>
                        setForm((current) => ({
                          ...current,

                          accountHead: option?.value || "",
                        }))
                      }
                      noOptionsMessage={() => "No Expense Head found"}
                    />
                  </div>

                  <div className="invoice-form-field">
                    <label>Status</label>

                    <select
                      value={form.status || "Draft"}
                      onChange={(e) =>
                        setForm({
                          ...form,

                          status: e.target.value,
                        })
                      }
                    >
                      <option value="Draft">Draft</option>

                      <option value="Posted">Posted</option>

                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="invoice-form-field invoice-textarea-field">
                  <label>Work Description</label>

                  <textarea
                    rows="3"
                    value={form.workDescription || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,

                        workDescription: e.target.value,
                      })
                    }
                    placeholder="Enter work description..."
                  />
                </div>
              </div>

              {/* ===========================================







                  GST DETAILS







              =========================================== */}

              <div className="invoice-section">
                <div className="invoice-section-title">Amount & GST</div>

                <div className="invoice-amount-layout">
                  <div className="invoice-form-grid amount-fields">
                    <div className="invoice-form-field">
                      <label>Taxable Amount *</label>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        value={form.taxableAmount}
                        onChange={(e) =>
                          setForm({
                            ...form,

                            taxableAmount: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="invoice-form-field">
                      <label>GST %</label>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.gstPercent}
                        onChange={(e) =>
                          setForm({
                            ...form,

                            gstPercent: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="invoice-form-field">
                      <label>GST Type</label>

                      <select
                        value={form.gstType}
                        onChange={(e) =>
                          setForm({
                            ...form,

                            gstType: e.target.value,
                          })
                        }
                      >
                        <option value="CGST_SGST">CGST + SGST</option>

                        <option value="IGST">IGST</option>
                      </select>
                    </div>
                  </div>

                  <div className="invoice-total-box">
                    <h3>Invoice Summary</h3>

                    <div className="invoice-total-row">
                      <span>Taxable</span>

                      <strong>{money(calculated.taxable)}</strong>
                    </div>

                    {form.gstType === "CGST_SGST" ? (
                      <>
                        <div className="invoice-total-row">
                          <span>CGST</span>

                          <strong>{money(calculated.cgst)}</strong>
                        </div>

                        <div className="invoice-total-row">
                          <span>SGST</span>

                          <strong>{money(calculated.sgst)}</strong>
                        </div>
                      </>
                    ) : (
                      <div className="invoice-total-row">
                        <span>IGST</span>

                        <strong>{money(calculated.igst)}</strong>
                      </div>
                    )}

                    <div className="invoice-total-row grand">
                      <span>Invoice Total</span>

                      <strong>{money(calculated.invoiceAmount)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===========================================







                  REMARK







              =========================================== */}

              <div className="invoice-section">
                <div className="invoice-form-field">
                  <label>Remark</label>

                  <textarea
                    rows="3"
                    value={form.remark || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,

                        remark: e.target.value,
                      })
                    }
                    placeholder="Enter remark..."
                  />
                </div>
              </div>

              {/* ===========================================







                  ATTACHMENTS







              =========================================== */}

              <div className="invoice-section invoice-attachment-section">
                <div className="invoice-section-title">
                  Supporting Documents
                </div>

                <div className="invoice-upload-box">
                  <label className="invoice-attach-btn">
                    + Attach Documents
                    <input
                      type="file"
                      multiple
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileChange}
                    />
                  </label>

                  <span className="invoice-upload-help">
                    PDF, Image, Word or Excel. Maximum 10 documents.
                  </span>
                </div>

                {/* EXISTING */}

                {existingAttachments.length > 0 && (
                  <div className="invoice-file-list">
                    <div className="invoice-file-heading">
                      Existing Documents
                    </div>

                    {existingAttachments.map((file, index) => (
                      <div
                        className="invoice-file-row"
                        key={file._id || file.fileName || index}
                      >
                        <button
                          type="button"
                          className="invoice-file-open"
                          onClick={() => openAttachment(file)}
                        >
                          <span>📎</span>

                          <span className="invoice-file-name">
                            {getAttachmentName(file, index)}
                          </span>

                          <span className="invoice-file-open-text">Open</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* NEW */}

                {attachments.length > 0 && (
                  <div className="invoice-file-list">
                    <div className="invoice-file-heading">New Documents</div>

                    {attachments.map((file, index) => (
                      <div
                        className="invoice-file-row"
                        key={`${file.name}-${index}`}
                      >
                        <div className="invoice-file-info">
                          <span>📎</span>

                          <div>
                            {file.name}

                            <small>
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </small>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="invoice-file-remove"
                          onClick={() => removeNewAttachment(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* =============================================







                MODAL FOOTER







            ============================================= */}

            <div className="invoice-modal-footer">
              <button
                type="button"
                className="invoice-btn"
                onClick={() => {
                  setOpen(false);

                  setEditing(null);
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="invoice-btn primary"
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editing
                    ? "Update Invoice"
                    : "Save Invoice"}
              </button>
            </div>
          </form>
        </div>
      )}

      {pay && (
        <div className="invoice-modal-backdrop">
          <form
            className="invoice-modal invoice-payment-modal"
            onSubmit={savePayment}
          >
            <div className="invoice-modal-head">
              <div>
                <h2>Add Payment</h2>

                <p>
                  {pay.invoiceNo}
                  {" • "}
                  Balance: {money(pay.balanceAmount)}
                </p>
              </div>

              <button
                type="button"
                className="invoice-modal-close"
                onClick={() => setPay(null)}
              >
                ×
              </button>
            </div>

            <div className="invoice-modal-body">
              <div className="invoice-form-grid payment-grid">
                <div className="invoice-form-field">
                  <label>Amount *</label>

                  <input
                    required
                    type="number"
                    min="0.01"
                    max={pay.balanceAmount}
                    step="0.01"
                    value={pay.amount}
                    onChange={(e) =>
                      setPay({
                        ...pay,

                        amount: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="invoice-form-field">
                  <label>Payment Date</label>

                  <input
                    type="date"
                    value={pay.date}
                    onChange={(e) =>
                      setPay({
                        ...pay,

                        date: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="invoice-form-field">
                  <label>Payment Mode</label>

                  <select
                    value={pay.paymentMode}
                    onChange={(e) =>
                      setPay({
                        ...pay,

                        paymentMode: e.target.value,
                      })
                    }
                  >
                    {[
                      "NEFT",
                      "Cheque",
                      "Cash",
                      "UPI",
                      "SVB-DBS",
                      "SVB-HDFC",
                      "SVB-IND",
                      "SDC-DBS",
                      "SDC-HDFC",
                      "DBS",
                      "HDFC",
                      "IND",
                    ].map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="invoice-form-field">
                  <label>Reference / UTR / Cheque</label>

                  <input
                    value={pay.referenceNo}
                    onChange={(e) =>
                      setPay({
                        ...pay,

                        referenceNo: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="invoice-form-field invoice-textarea-field">
                <label>Remark</label>

                <textarea
                  rows="3"
                  value={pay.remark}
                  onChange={(e) =>
                    setPay({
                      ...pay,

                      remark: e.target.value,
                    })
                  }
                />
              </div>
            </div>

            <div className="invoice-modal-footer">
              <button
                type="button"
                className="invoice-btn"
                onClick={() => setPay(null)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="invoice-btn primary"
                disabled={paymentSaving}
              >
                {paymentSaving ? "Saving..." : "Save Payment"}
              </button>
            </div>
          </form>
        </div>
      )}

      {preview && (
        <div className="invoice-preview-overlay">
          <div className="invoice-preview-shell">
            <div className="invoice-preview-actions invoice-no-print">
              <div>
                <strong>Purchase Invoice</strong>

                <span>{preview.invoiceNo}</span>
              </div>

              <div className="invoice-preview-action-buttons">
                <button type="button" onClick={() => window.print()}>
                  Print / Save PDF
                </button>

                <button type="button" onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>

            <div className="invoice-a4">
              <div className="invoice-pdf-header">
                <div className="invoice-pdf-brand">
                  <div className="invoice-pdf-logo">SDC</div>

                  <div>
                    <h1>SDC Studio LLP</h1>

                    <p>Purchase Department</p>
                  </div>
                </div>

                <div className="invoice-pdf-title">
                  <h2>PURCHASE INVOICE</h2>

                  <div>
                    <span>Invoice No.</span>

                    <strong>{preview.invoiceNo || "-"}</strong>
                  </div>

                  <div>
                    <span>Invoice Date</span>

                    <strong>{dateText(preview.invoiceDate)}</strong>
                  </div>

                  <div>
                    <span>Vendor Bill</span>

                    <strong>{preview.billNo || "-"}</strong>
                  </div>
                </div>
              </div>

              <div className="invoice-pdf-accent" />

              {/* ===========================================







                  INFORMATION







              =========================================== */}

              <div className="invoice-pdf-info-grid">
                <div className="invoice-pdf-info-box">
                  <div className="invoice-pdf-info-title">VENDOR DETAILS</div>

                  <h3>{preview.vendorName || "-"}</h3>

                  {preview.contactPerson && (
                    <p>Contact: {preview.contactPerson}</p>
                  )}

                  {preview.contactNo && <p>Mobile: {preview.contactNo}</p>}

                  {preview.gstNo && <p>GST No.: {preview.gstNo}</p>}
                </div>

                <div className="invoice-pdf-info-box">
                  <div className="invoice-pdf-info-title">
                    INVOICE INFORMATION
                  </div>

                  <div className="invoice-pdf-meta-row">
                    <span>Company</span>

                    <strong>SDC</strong>
                  </div>

                  <div className="invoice-pdf-meta-row">
                    <span>Project</span>

                    <strong>{preview.projectName || "-"}</strong>
                  </div>

                  <div className="invoice-pdf-meta-row">
                    <span>Work Status</span>

                    <strong>{preview.workStatus || "-"}</strong>
                  </div>

                  <div className="invoice-pdf-meta-row">
                    <span>Account Head</span>

                    <strong>{preview.accountHead || "-"}</strong>
                  </div>
                </div>
              </div>

              {/* ===========================================







                  WORK







              =========================================== */}

              {preview.workDescription && (
                <div className="invoice-pdf-work">
                  <div className="invoice-pdf-section-title">
                    Work Description
                  </div>

                  <p>{preview.workDescription}</p>
                </div>
              )}

              {/* ===========================================







                  AMOUNT TABLE







              =========================================== */}

              <div className="invoice-pdf-section-title">Invoice Amount</div>

              <table className="invoice-pdf-amount-table">
                <thead>
                  <tr>
                    <th>DESCRIPTION</th>

                    <th>AMOUNT</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>Taxable Amount</td>

                    <td>{money(preview.taxableAmount)}</td>
                  </tr>

                  {preview.gstType === "IGST" ? (
                    <tr>
                      <td>
                        IGST ({preview.gstPercent}
                        %)
                      </td>

                      <td>{money(preview.igstAmount)}</td>
                    </tr>
                  ) : (
                    <>
                      <tr>
                        <td>
                          CGST ({Number(preview.gstPercent || 0) / 2}
                          %)
                        </td>

                        <td>{money(preview.cgstAmount)}</td>
                      </tr>

                      <tr>
                        <td>
                          SGST ({Number(preview.gstPercent || 0) / 2}
                          %)
                        </td>

                        <td>{money(preview.sgstAmount)}</td>
                      </tr>
                    </>
                  )}

                  <tr className="invoice-pdf-grand">
                    <td>INVOICE TOTAL</td>

                    <td>{money(preview.invoiceAmount)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="invoice-pdf-payment-grid">
                <div className="invoice-pdf-payment-card">
                  <span>Invoice Amount</span>

                  <strong>{money(preview.invoiceAmount)}</strong>
                </div>

                <div className="invoice-pdf-payment-card">
                  <span>Paid Amount</span>

                  <strong>{money(preview.paidAmount)}</strong>
                </div>

                <div className="invoice-pdf-payment-card">
                  <span>Balance</span>

                  <strong>{money(preview.balanceAmount)}</strong>
                </div>

                <div className="invoice-pdf-payment-card">
                  <span>Payment Status</span>

                  <strong>{preview.paymentStatus || "Unpaid"}</strong>
                </div>
              </div>

              {Array.isArray(preview.payments) &&
                preview.payments.length > 0 && (
                  <div className="invoice-pdf-payment-history">
                    <div className="invoice-pdf-section-title">
                      Payment History
                    </div>

                    <table className="invoice-pdf-history-table">
                      <thead>
                        <tr>
                          <th>DATE</th>

                          <th>MODE</th>

                          <th>REFERENCE</th>

                          <th>AMOUNT</th>
                        </tr>
                      </thead>

                      <tbody>
                        {preview.payments.map((payment, index) => (
                          <tr key={payment._id || index}>
                            <td>{dateText(payment.date)}</td>

                            <td>{payment.paymentMode || "-"}</td>

                            <td>{payment.referenceNo || "-"}</td>

                            <td>{money(payment.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              {preview.remark && (
                <div className="invoice-pdf-notes">
                  <h4>Remark</h4>

                  <p>{preview.remark}</p>
                </div>
              )}

              {Array.isArray(preview.attachments) &&
                preview.attachments.length > 0 && (
                  <div className="invoice-pdf-attachments invoice-no-print">
                    <h4>Supporting Documents</h4>

                    <div className="invoice-pdf-document-list">
                      {preview.attachments.map((file, index) => (
                        <button
                          type="button"
                          key={file._id || file.fileName || index}
                          className="invoice-pdf-document-btn"
                          onClick={() => openAttachment(file)}
                        >
                          📎 {getAttachmentName(file, index)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              <div className="invoice-pdf-footer">
                <small>
                  This is a system-generated purchase invoice record.
                </small>

                <div className="invoice-signature">
                  <div className="invoice-signature-line" />

                  <strong>Authorized Signatory</strong>

                  <span>SDC Studio LLP</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
